<template>
  <div class="story-edit-page">
    <div class="edit-header">
      <el-button icon="el-icon-arrow-left" @click="$router.push('/story-plays')">
        返回
      </el-button>
      <h2 style="margin: 0 16px; flex: 1;">{{ storyTitle }}</h2>
      <!-- 播放模式切换：从头播放 vs 从当前场景播放 -->
      <el-switch
        v-model="playFromBeginning"
        active-text="从头播放"
        style="margin-right: 12px;"
        @change="_persistEditPrefs"
      />
      <el-button type="primary" @click="handlePreview">预览</el-button>
    </div>

    <div class="edit-body">
      <!-- Left: Scene List -->
      <div class="scene-list">
        <div class="scene-list-header scene-list-toolbar">
          <span>场景列表</span>
          <el-select
            v-model="annotationFilter"
            size="mini"
            placeholder="批注筛选"
            class="scene-annotation-filter"
          >
            <el-option label="全部" value="all" />
            <el-option label="无批注" value="none" />
            <el-option label="未处理" value="pending" />
            <el-option label="等待审核" value="reviewing" />
            <el-option label="已处理" value="processed" />
          </el-select>
          <el-select
            v-model="jumpFilter"
            size="mini"
            placeholder="跳转筛选"
            class="scene-jump-filter"
          >
            <el-option label="全部" value="all" />
            <el-option label="有跳转" value="has" />
            <el-option label="无跳转" value="none" />
          </el-select>
          <el-select
            v-model="sourceFilter"
            size="mini"
            placeholder="来源筛选"
            class="scene-source-filter"
          >
            <el-option label="全部" value="all" />
            <el-option label="有来源" value="has" />
            <el-option label="无来源" value="none" />
          </el-select>
          <el-select
            v-model="resourceFilter"
            size="mini"
            placeholder="资源筛选"
            class="scene-resource-filter"
          >
            <el-option label="全部" value="all" />
            <el-option label="有资源" value="has" />
            <el-option label="无资源" value="none" />
          </el-select>
          <el-select
            v-model="dubFilter"
            size="mini"
            placeholder="配音筛选"
            class="scene-dub-filter"
          >
            <el-option label="全部" value="all" />
            <el-option label="有配音" value="has" />
            <el-option label="无配音" value="none" />
          </el-select>
          <el-select
            v-model="bgTypeFilter"
            size="mini"
            placeholder="类型筛选"
            class="scene-bg-type-filter"
          >
            <el-option label="全部" value="all" />
            <el-option label="图片" value="image" />
            <el-option label="视频" value="video" />
            <el-option label="小游戏" value="minigame" />
            <el-option label="三方游戏" value="third_party_game" />
          </el-select>
          <el-select
            v-model="resourceMissingFilter"
            size="mini"
            placeholder="资源缺失筛选"
            class="scene-missing-filter"
          >
            <el-option label="全部" value="all" />
            <el-option label="资源缺失" value="missing" />
            <el-option label="资源正常" value="ok" />
          </el-select>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAddScene">
            添加场景
          </el-button>
          <el-button
            type="warning"
            size="mini"
            icon="el-icon-microphone"
            :loading="globalBatchTTSLoading"
            @click="handleGlobalBatchTTS"
          >
            一键全剧配音
          </el-button>
          <el-button
            type="danger"
            size="mini"
            icon="el-icon-warning"
            :loading="resourceChecking"
            @click="handleCheckResources"
          >
            检查资源
          </el-button>
        </div>
        <draggable
          v-if="annotationFilter === 'all' && jumpFilter === 'all' && sourceFilter === 'all' && resourceFilter === 'all' && dubFilter === 'all' && bgTypeFilter === 'all' && resourceMissingFilter === 'all'"
          v-model="scenes"
          class="scene-list-body"
          handle=".scene-drag-handle"
          @end="handleDragEnd"
        >
          <div
            v-for="(scene, index) in scenes"
            :key="scene.id || index"
            class="scene-item"
            :class="{ active: selectedScene && selectedScene.id === scene.id }"
            @click="selectScene(scene)"
          >
            <span class="scene-drag-handle" style="cursor: move; color: #c0c4cc;">
              <i class="el-icon-rank" />
            </span>
            <span class="scene-index">{{ index + 1 }}</span>
            <div v-if="scene.bg_image" class="scene-thumb-wrap">
              <video
                v-if="sceneBgIsVideo(scene)"
                :src="resolveMediaUrl(scene.bg_image)"
                class="scene-thumb scene-thumb-video"
                muted
                preload="metadata"
                playsinline
              />
              <el-image
                v-else
                :src="resolveMediaUrl(scene.bg_image)"
                :preview-src-list="[resolveMediaUrl(scene.bg_image)]"
                fit="cover"
                class="scene-thumb"
              />
            </div>
            <i
              v-if="!scene.bg_image && !isGameBgType(scene.bg_type)"
              class="el-icon-warning-outline scene-no-bg-icon"
              style="color: #E6A23C;"
              title="无背景"
            />
            <i
              v-else-if="isGameBgType(scene.bg_type)"
              class="el-icon-cpu scene-no-bg-icon"
              style="color: #409EFF;"
              :title="getBgTypeLabel(scene.bg_type)"
            />
            <div class="scene-preview-block">
              <span class="scene-preview">{{ getScenePreview(scene) }}</span>
              <div class="scene-status-row">
                <el-tag
                  size="mini"
                  :type="getBgTypeTagType(scene.bg_type)"
                  effect="plain"
                  class="scene-type-tag"
                >
                  {{ getBgTypeLabel(scene.bg_type) }}
                </el-tag>
                <el-tag
                  v-if="sceneHasAnnotation(scene)"
                  :type="annotationTagType(scene.annotation_status)"
                  size="mini"
                  class="scene-ann-tag"
                >
                  {{ annotationStatusLabel(scene) }}
                </el-tag>
                <el-tooltip v-if="isSceneMissingDubbing(scene)" content="配音配置不完整" placement="top">
                  <i class="el-icon-microphone scene-dubbing-warning" />
                </el-tooltip>
                <el-tag
                  v-if="!sceneHasSource(scene)"
                  size="mini"
                  type="danger"
                  effect="dark"
                  class="scene-source-tag"
                >
                  无来源
                </el-tag>
                <el-tag
                  v-if="resourceMissingMap[scene.id] === false"
                  size="mini"
                  type="danger"
                  effect="dark"
                  class="scene-missing-tag"
                >
                  资源缺失
                </el-tag>
              </div>
              <!-- Flow indicator -->
              <div class="scene-flow-row">
                <template v-if="scene.options && scene.options.length > 0">
                  <div v-for="(opt, oi) in scene.options" :key="oi" class="scene-opt-flow">
                    <span class="flow-prefix">{{ oi === 0 ? '选择' : '' }}</span>
                    <span class="flow-text">「{{ opt.text || '...' }}」</span>
                    <span class="flow-arrow">→</span>
                    <template v-if="opt.next_scene_sort != null">
                      <span class="flow-target">{{ getSceneNameBySort(opt.next_scene_sort) }}</span>
                      <span v-if="opt.skip_to_end" class="flow-tail-suffix">(末尾)</span>
                    </template>
                    <span v-else class="flow-unknown">?</span>
                  </div>
                </template>
                <template v-else>
                  <span>
                    <span class="flow-arrow">→</span>
                    <span v-if="scene.next_scene_sort != null" class="flow-target">
                      {{ getSceneNameBySort(scene.next_scene_sort) }}
                      <span v-if="scene.next_scene_skip_to_end" class="flow-tail-suffix">(末尾)</span>
                    </span>
                    <span v-else-if="scene.mini_game_id" class="flow-target-unknown">{{ getBgTypeLabel(scene.bg_type) }}</span>
                    <span v-else class="flow-target-unknown">无跳转</span>
                  </span>
                </template>
              </div>
            </div>
            <i
              class="el-icon-delete scene-delete"
              @click.stop="handleDeleteScene(index)"
            />
            <el-button
              type="text"
              size="mini"
              class="scene-order-btn"
              @click.stop="handleChangeSceneOrder(index)"
            >
              顺序
            </el-button>
          </div>
        </draggable>
        <div v-else class="scene-list-body scene-list-static">
          <div
            v-for="(scene, index) in filteredScenes"
            :key="scene.id || index"
            class="scene-item"
            :class="{ active: selectedScene && selectedScene.id === scene.id }"
            @click="selectScene(scene)"
          >
            <span class="scene-index scene-index-static">{{ filteredSceneIndex(scene) }}</span>
            <div v-if="scene.bg_image" class="scene-thumb-wrap">
              <video
                v-if="sceneBgIsVideo(scene)"
                :src="resolveMediaUrl(scene.bg_image)"
                class="scene-thumb scene-thumb-video"
                muted
                preload="metadata"
                playsinline
              />
              <el-image
                v-else
                :src="resolveMediaUrl(scene.bg_image)"
                :preview-src-list="[resolveMediaUrl(scene.bg_image)]"
                fit="cover"
                class="scene-thumb"
              />
            </div>
            <i
              v-if="!scene.bg_image && !isGameBgType(scene.bg_type)"
              class="el-icon-warning-outline scene-no-bg-icon"
              style="color: #E6A23C;"
              title="无背景"
            />
            <i
              v-else-if="isGameBgType(scene.bg_type)"
              class="el-icon-cpu scene-no-bg-icon"
              style="color: #409EFF;"
              :title="getBgTypeLabel(scene.bg_type)"
            />
            <div class="scene-preview-block">
              <span class="scene-preview">{{ getScenePreview(scene) }}</span>
              <div class="scene-status-row">
                <el-tag
                  size="mini"
                  :type="getBgTypeTagType(scene.bg_type)"
                  effect="plain"
                  class="scene-type-tag"
                >
                  {{ getBgTypeLabel(scene.bg_type) }}
                </el-tag>
                <el-tag
                  v-if="sceneHasAnnotation(scene)"
                  :type="annotationTagType(scene.annotation_status)"
                  size="mini"
                  class="scene-ann-tag"
                >
                  {{ annotationStatusLabel(scene) }}
                </el-tag>
                <el-tooltip v-if="isSceneMissingDubbing(scene)" content="配音配置不完整" placement="top">
                  <i class="el-icon-microphone scene-dubbing-warning" />
                </el-tooltip>
                <el-tag
                  v-if="!sceneHasSource(scene)"
                  size="mini"
                  type="danger"
                  effect="dark"
                  class="scene-source-tag"
                >
                  无来源
                </el-tag>
                <el-tag
                  v-if="resourceMissingMap[scene.id] === false"
                  size="mini"
                  type="danger"
                  effect="dark"
                  class="scene-missing-tag"
                >
                  资源缺失
                </el-tag>
              </div>
              <!-- Flow indicator -->
              <div class="scene-flow-row">
                <template v-if="scene.options && scene.options.length > 0">
                  <div v-for="(opt, oi) in scene.options" :key="oi" class="scene-opt-flow">
                    <span class="flow-prefix">{{ oi === 0 ? '选择' : '' }}</span>
                    <span class="flow-text">「{{ opt.text || '...' }}」</span>
                    <span class="flow-arrow">→</span>
                    <template v-if="opt.next_scene_sort != null">
                      <span class="flow-target">{{ getSceneNameBySort(opt.next_scene_sort) }}</span>
                      <span v-if="opt.skip_to_end" class="flow-tail-suffix">(末尾)</span>
                    </template>
                    <span v-else class="flow-unknown">?</span>
                  </div>
                </template>
                <template v-else>
                  <span>
                    <span class="flow-arrow">→</span>
                    <span v-if="scene.next_scene_sort != null" class="flow-target">
                      {{ getSceneNameBySort(scene.next_scene_sort) }}
                      <span v-if="scene.next_scene_skip_to_end" class="flow-tail-suffix">(末尾)</span>
                    </span>
                    <span v-else-if="scene.mini_game_id" class="flow-target-unknown">{{ getBgTypeLabel(scene.bg_type) }}</span>
                    <span v-else class="flow-target-unknown">无跳转</span>
                  </span>
                </template>
              </div>
            </div>
            <i
              class="el-icon-delete scene-delete"
              @click.stop="handleDeleteSceneById(scene.id)"
            />
            <el-button
              type="text"
              size="mini"
              class="scene-order-btn"
              @click.stop="handleChangeSceneOrderBySceneId(scene.id)"
            >
              顺序
            </el-button>
          </div>
          <div v-if="filteredScenes.length === 0" class="scene-list-empty-tip">
            无匹配场景
          </div>
        </div>
      </div>

      <!-- Right: Scene Editor -->
      <div v-if="selectedScene" class="scene-editor">
        <div class="scene-editor-head">
          <h3 class="scene-editor-title">场景编辑</h3>
          <el-button
            class="scene-editor-save-float"
            type="primary"
            size="small"
            :loading="saving"
            :disabled="bgUploading || annotationUploading"
            @click="handleSaveScene"
          >
            保存
          </el-button>
        </div>
        <div class="scene-editor-body">
          <el-form label-width="90px">
          <el-form-item label="场景名称">
            <el-input v-model="editorForm.name" placeholder="输入场景名称（可选）" />
          </el-form-item>

          <el-form-item label="来源">
            <template v-if="sceneSources.length">
              <el-tag
                v-for="s in sceneSources"
                :key="s.id"
                size="small"
                style="margin: 2px 4px 2px 0; cursor: pointer"
                @click="selectScene(s)"
              >
                {{ getScenePreview(s) || '#' + s.sort_order }}
              </el-tag>
            </template>
            <span v-else style="color: #909399; font-size: 13px;">无（起始场景）</span>
          </el-form-item>

          <el-form-item label="类型">
            <el-radio-group
              v-model="editorForm.bg_type"
              size="small"
              class="bg-type-radio"
              @change="handleEditorBgTypeChange"
            >
              <el-radio-button label="image">图片</el-radio-button>
              <el-radio-button label="video">视频</el-radio-button>
              <el-radio-button label="minigame">小游戏</el-radio-button>
              <el-radio-button label="third_party_game">三方游戏</el-radio-button>
            </el-radio-group>
            <div v-if="!isGameBgType(editorForm.bg_type)" class="bg-uploader-wrap">
              <el-upload
                ref="bgUpload"
                class="bg-upload-trigger-hidden"
                :show-file-list="false"
                :http-request="handleBgUpload"
                action="#"
                :accept="editorForm.bg_type === 'video' ? 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov' : 'image/*'"
              />
              <div
                v-if="!editorForm.bg_image && !bgUploading"
                class="bg-uploader"
                @click="triggerBgUpload"
              >
                <div class="bg-upload-inner">
                  <i class="el-icon-plus bg-uploader-icon" />
                </div>
              </div>
              <div v-else-if="bgUploading" class="bg-uploader">
                <div class="upload-progress">
                  <el-progress type="circle" :percentage="bgUploadProgress" :width="80" />
                </div>
              </div>
              <div v-else class="bg-uploader bg-uploader-filled">
                <video
                  v-if="editorForm.bg_type === 'video'"
                  :src="resolveMediaUrl(editorForm.bg_image)"
                  class="bg-preview-el bg-preview-video"
                  controls
                  muted
                  playsinline
                />
                <el-image
                  v-else
                  :src="resolveMediaUrl(editorForm.bg_image)"
                  :preview-src-list="[resolveMediaUrl(editorForm.bg_image)]"
                  fit="cover"
                  class="bg-preview-el"
                />
              </div>
              <div class="bg-uploader-actions">
                <el-button size="mini" type="primary" plain @click="triggerBgUpload">
                  {{
                    editorForm.bg_image
                      ? editorForm.bg_type === "video"
                        ? "更换视频"
                        : "更换图片"
                      : editorForm.bg_type === "video"
                        ? "上传视频"
                        : "上传图片"
                  }}
                </el-button>
                <el-button
                  v-if="editorForm.bg_image"
                  size="mini"
                  type="text"
                  @click.stop="editorForm.bg_image = ''"
                >
                  移除背景
                </el-button>
              </div>
            </div>
          </el-form-item>

          <el-form-item v-if="!isGameBgType(editorForm.bg_type)" label="背景提示词">
            <!-- 资产提示功能容器 -->
            <div class="mention-wrapper">
              <el-input
                ref="bgPromptInput"
                v-model="editorForm.bg_prompt"
                type="textarea"
                :rows="3"
                placeholder="输入背景图生成提示词（可选），支持 @ 插入资产"
                @input="handlePromptInput"
                @keydown.native="handlePromptKeyDown"
              />
              <!-- 资产选择列表弹窗 -->
              <div v-if="mentionVisible" class="mention-list" :style="{ top: mentionPosition.top + 'px', left: mentionPosition.left + 'px' }">
                <div
                  v-for="(asset, index) in filteredMentionAssets"
                  :key="asset.id"
                  class="mention-item"
                  :class="{ active: index === mentionIndex }"
                  @click="selectMention(asset)"
                >
                  <span class="mention-item-type">{{ asset.asset_type }}</span>
                  <span class="mention-item-name">{{ asset.name }}</span>
                </div>
                <div v-if="!filteredMentionAssets.length" class="mention-empty">无匹配资产</div>
              </div>
              <!-- 用于计算 @ 符号位置的镜像元素 -->
              <div ref="mentionMirror" class="mention-mirror"></div>
            </div>
            <div style="margin-top: 8px;">
              <el-button
                size="mini"
                type="primary"
                icon="el-icon-download"
                :loading="downloadingAssets"
                @click="handleDownloadAssets"
              >
                下载匹配资产
              </el-button>
            </div>
          </el-form-item>

          <el-form-item label="批注">
            <div class="scene-annotation-editor">
              <el-input
                v-model="editorForm.annotation_text"
                type="textarea"
                :rows="3"
                placeholder="批注文字（可选）"
              />
              <div class="annotation-img-row">
                <el-image
                  v-if="editorForm.annotation_image"
                  :src="resolveMediaUrl(editorForm.annotation_image)"
                  :preview-src-list="[resolveMediaUrl(editorForm.annotation_image)]"
                  fit="cover"
                  class="annotation-editor-img"
                />
                <el-upload
                  :show-file-list="false"
                  :http-request="handleAnnotationUpload"
                  action="#"
                  accept="image/*"
                  :disabled="annotationUploading"
                >
                  <el-button size="mini" :loading="annotationUploading" :disabled="annotationUploading">
                    {{ editorForm.annotation_image ? "更换批注图" : "上传批注图" }}
                  </el-button>
                </el-upload>
                <el-button
                  v-if="editorForm.annotation_image"
                  size="mini"
                  type="danger"
                  plain
                  :disabled="annotationUploading"
                  @click="editorForm.annotation_image = ''"
                >
                  清空图片
                </el-button>
              </div>
              <el-radio-group
                v-if="annotationEditorHasContent"
                v-model="editorForm.annotation_status"
                size="small"
                :disabled="annotationUploading"
              >
                <el-radio label="pending">未处理</el-radio>
                <el-radio label="reviewing">等待审核</el-radio>
                <el-radio label="processed">已处理</el-radio>
              </el-radio-group>
            </div>
          </el-form-item>

          <el-form-item v-if="!isGameBgType(editorForm.bg_type)" label="台词">
            <div style="margin-bottom: 12px; display: flex; align-items: center; gap: 10px;">
              <el-button
                type="primary"
                size="mini"
                icon="el-icon-microphone"
                :loading="batchTTSLoading"
                @click="handleBatchGenerateTTS"
              >
                一键生成全部配音
              </el-button>
              <span v-if="batchTTSLoading" style="font-size: 12px; color: #409EFF;">
                正在批量生成中，请稍候...
              </span>
            </div>
            <div
              v-for="(line, idx) in editorForm.lines"
              :key="idx"
              class="line-input-row"
            >
              <div class="line-config-main">
                <el-input
                  v-model="editorForm.lines[idx].text"
                  type="textarea"
                  :rows="2"
                  placeholder="请输入台词"
                  @change="handleLineContentChange(idx)"
                />
                <div class="line-voice-row">
                  <el-select
                    v-model="editorForm.lines[idx].speaker_asset_id"
                    placeholder="选择角色"
                    clearable
                    filterable
                    style="width: 220px;"
                    @change="handleLineSpeakerChange(idx)"
                  >
                    <el-option
                      v-for="asset in characterAssets"
                      :key="asset.id"
                      :label="asset.name"
                      :value="asset.id"
                    />
                  </el-select>
                  <el-select
                    v-model="editorForm.lines[idx].emotion"
                    placeholder="选择情绪"
                    clearable
                    allow-create
                    filterable
                    default-first-option
                    style="width: 220px;"
                    @change="handleLineContentChange(idx)"
                  >
                    <el-option
                      v-for="emotion in getLineEmotionOptions(editorForm.lines[idx])"
                      :key="emotion"
                      :label="emotionMap[emotion] || emotion"
                      :value="emotion"
                    />
                  </el-select>
                  <div class="line-scale-wrap">
                    <span class="scale-label">强度</span>
                    <el-input-number
                      v-model="editorForm.lines[idx].emotion_scale"
                      :min="0.1"
                      :max="2.0"
                      :step="0.1"
                      size="mini"
                      controls-position="right"
                      style="width: 90px"
                      @change="handleLineContentChange(idx)"
                    />
                  </div>
                  <div class="line-speed-wrap">
                    <span class="scale-label">语速</span>
                    <el-slider
                      v-model="editorForm.lines[idx].speech_rate"
                      :min="0.5"
                      :max="2.0"
                      :step="0.1"
                      :format-tooltip="val => val != null ? val.toFixed(1) + 'x' : ''"
                      style="width: 120px; margin-left: 8px; margin-right: 8px;"
                      @change="handleLineContentChange(idx)"
                    />
                  </div>
                  <div v-if="editorForm.lines[idx].speaker_asset_id && editorForm.lines[idx].emotion" class="line-tts-wrap">
                    <template v-if="editorForm.lines[idx].audio_url">
                      <el-button
                        type="success"
                        size="mini"
                        icon="el-icon-video-play"
                        circle
                        title="播放配音"
                        @click="playLineTTS(editorForm.lines[idx])"
                      />
                      <el-button
                        type="text"
                        size="mini"
                        style="margin-left: 4px"
                        @click="generateLineTTS(idx)"
                      >
                        重新生成
                      </el-button>
                    </template>
                    <el-button
                      v-else
                      type="primary"
                      size="mini"
                      :loading="editorForm.lines[idx].ttsLoading"
                      @click="generateLineTTS(idx)"
                    >
                      生成
                    </el-button>
                  </div>
                </div>
              </div>
              <el-button
                type="danger"
                size="mini"
                icon="el-icon-delete"
                circle
                @click="removeLine(idx)"
              />
            </div>
            <el-button type="text" icon="el-icon-plus" @click="addLine">
              添加台词
            </el-button>
          </el-form-item>

          <el-form-item v-if="!isGameBgType(editorForm.bg_type)" label="选项分支">
            <div
              v-for="(opt, idx) in editorForm.options"
              :key="idx"
              class="option-input-row"
            >
              <el-input
                v-model="editorForm.options[idx].text"
                placeholder="选项文本"
                style="flex: 1;"
              />
              <el-select
                v-model="editorForm.options[idx].next_scene_sort"
                placeholder="跳转场景"
                clearable
                filterable
                style="width: 140px;"
              >
                <el-option
                  v-for="s in availableTargetScenes"
                  :key="s.sort_order"
                  :label="`${s.sort_order}. ${getScenePreview(s)}`"
                  :value="s.sort_order"
                />
              </el-select>
              <el-checkbox
                v-model="editorForm.options[idx].skip_to_end"
                title="跳转到该场景最后一句"
              >
                跳至末尾
              </el-checkbox>
              <el-button
                type="danger"
                size="mini"
                icon="el-icon-delete"
                circle
                @click="removeOption(idx)"
              />
            </div>
            <el-button type="text" icon="el-icon-plus" @click="addOption">
              添加选项
            </el-button>
          </el-form-item>

          <el-form-item v-if="!isGameBgType(editorForm.bg_type) && !editorForm.options.length" label="默认跳转">
            <div style="display: flex; gap: 12px; align-items: center;">
              <el-select
                v-model="editorForm.next_scene_sort"
                placeholder="选择跳转场景"
                clearable
                filterable
                style="width: 200px;"
              >
                <el-option
                  v-for="s in availableTargetScenes"
                  :key="s.sort_order"
                  :label="`${s.sort_order}. ${getScenePreview(s)}`"
                  :value="s.sort_order"
                />
              </el-select>
              <el-checkbox
                v-model="editorForm.next_scene_skip_to_end"
                title="跳转到该场景最后一句"
              >
                跳至末尾
              </el-checkbox>
            </div>
          </el-form-item>

          <el-form-item v-if="editorForm.bg_type === 'minigame'" label="选择小游戏">
            <el-select
              v-model="editorForm.mini_game_id"
              placeholder="请选择小游戏"
              clearable
              style="width: 300px;"
            >
              <el-option
                v-for="game in miniGames"
                :key="game.id"
                :label="game.name"
                :value="game.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item v-if="editorForm.bg_type === 'third_party_game'" label="选择三方游戏">
            <el-select
              v-model="editorForm.third_party_game_id"
              placeholder="请选择三方游戏"
              clearable
              style="width: 300px;"
              @change="handleThirdPartyGameChange"
            >
              <el-option
                v-for="game in thirdPartyGames"
                :key="game.id"
                :label="game.name"
                :value="game.game_id"
              />
            </el-select>
          </el-form-item>

          <el-form-item v-if="isGameBgType(editorForm.bg_type)" label="描述">
            <el-input
              v-model="editorForm.mini_game_desc"
              type="textarea"
              :rows="3"
              placeholder="请输入游戏描述"
              style="width: 500px;"
            />
          </el-form-item>

          <el-form-item v-if="isGameBgType(editorForm.bg_type)" label="通关跳转">
            <div style="display: flex; gap: 12px; align-items: center;">
              <el-select
                v-model="editorForm.next_scene_sort"
                placeholder="通关后跳转场景"
                clearable
                filterable
                style="width: 200px;"
              >
                <el-option
                  v-for="s in availableTargetScenes"
                  :key="s.sort_order"
                  :label="`${s.sort_order}. ${getScenePreview(s)}`"
                  :value="s.sort_order"
                />
              </el-select>
              <el-checkbox
                v-model="editorForm.next_scene_skip_to_end"
                title="跳转到该场景最后一句"
              >
                跳至末尾
              </el-checkbox>
            </div>
          </el-form-item>

          <el-form-item v-if="isGameBgType(editorForm.bg_type)" label="失败跳转">
            <div style="display: flex; gap: 12px; align-items: center;">
              <el-select
                v-model="editorForm.game_fail_next_scene_sort"
                placeholder="失败后跳转场景"
                clearable
                filterable
                style="width: 200px;"
              >
                <el-option
                  v-for="s in availableTargetScenes"
                  :key="s.sort_order"
                  :label="`${s.sort_order}. ${getScenePreview(s)}`"
                  :value="s.sort_order"
                />
              </el-select>
              <el-checkbox
                v-model="editorForm.game_fail_next_scene_skip_to_end"
                title="跳转到该场景最后一句"
              >
                跳至末尾
              </el-checkbox>
            </div>
          </el-form-item>
          </el-form>
        </div>
      </div>

      <div v-else class="scene-editor" style="display: flex; align-items: center; justify-content: center; color: #909399;">
        请选择一个场景进行编辑
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import {
  getStoryPlay,
  getStoryScenes,
  createStoryScene,
  updateStoryScene,
  deleteStoryScene,
  reorderStoryScenes,
  uploadStoryImage,
  uploadStoryVideo,
  getAssets,
  downloadSceneAssets,
  getMiniGames,
  checkStoryPlayResources,
  getThirdPartyGames
} from "@/net/api";
import { resolveMediaUrl } from "@/utils/media";
import { EMOTION_MAP } from "@/config/emotions";

export default {
  name: "StoryPlayEdit",
  components: {
    draggable
  },
  data() {
    return {
      storyId: null,
      saving: false,
      batchTTSLoading: false,
      globalBatchTTSLoading: false,
      storyTitle: "",
      scenes: [],
      miniGames: [],
      thirdPartyGames: [],
      selectedScene: null,
      editorForm: {
        bg_type: "image",
        bg_image: "",
        lines: [],
        options: [],
        next_scene_sort: null,
        next_scene_skip_to_end: false,
        name: "",
        bg_prompt: "",
        annotation_text: "",
        annotation_image: "",
        annotation_status: "pending",
        mini_game_id: null,
        mini_game_desc: "",
        third_party_game_id: "",
        third_party_game_name: "",
        game_fail_next_scene_sort: null,
        game_fail_next_scene_skip_to_end: false
      },
      saving: false,
      annotationFilter: "all",
      jumpFilter: "all",
      sourceFilter: "all",
      resourceFilter: "all",
      dubFilter: "all",
      bgTypeFilter: "all",
      resourceMissingFilter: "all",
      playFromBeginning: true, // 默认从头播放；离开再进入页由 sessionStorage 恢复
      bgUploading: false, // 是否正在上传背景图
      bgUploadProgress: 0, // 上传进度百分比
      downloadingAssets: false, // 是否正在打包下载
      annotationUploading: false,
      resourceChecking: false, // 资源文件检查中
      resourceMissingMap: {}, // scene_id -> boolean (true=存在, false=缺失)
      // 资产提示相关状态
      allAssets: [],
      mentionVisible: false,
      mentionQuery: "",
      mentionIndex: 0,
      mentionPosition: { top: 0, left: 0 },
      bgUploadAbortController: null,
      annotationUploadAbortController: null,
      emotionMap: EMOTION_MAP
    };
  },
  computed: {
    sceneLookupBySort() {
      const map = {};
      (this.scenes || []).forEach((s) => {
        map[s.sort_order] = s;
      });
      return map;
    },
    annotationEditorHasContent() {
      const t = (this.editorForm.annotation_text || "").trim();
      const img = (this.editorForm.annotation_image || "").trim();
      return !!(t || img);
    },
    filteredScenes() {
      let list = this.scenes || [];

      // Annotation filter
      const af = this.annotationFilter;
      if (af && af !== "all") {
        list = list.filter((s) => {
          const has = this.sceneHasAnnotation(s);
          if (af === "none") return !has;
          if (!has) return false;
          const st = (s.annotation_status || "pending").trim();
          return st === af;
        });
      }

      // Jump filter
      const jf = this.jumpFilter;
      if (jf && jf !== "all") {
        list = list.filter((s) => {
          const has = this.sceneHasJump(s);
          return jf === "has" ? has : !has;
        });
      }

      // Source filter
      const sf = this.sourceFilter;
      if (sf && sf !== "all") {
        list = list.filter((s) => {
          const has = this.sceneHasSource(s);
          return sf === "has" ? has : !has;
        });
      }

      // Resource filter
      const rf = this.resourceFilter;
      if (rf && rf !== "all") {
        list = list.filter((s) => {
          const has = !!(s.bg_image);
          return rf === "has" ? has : !has;
        });
      }

      // Dub filter
      const df = this.dubFilter;
      if (df && df !== "all") {
        list = list.filter((s) => {
          const has = this.sceneHasDub(s);
          return df === "has" ? has : !has;
        });
      }

      // BgType filter
      const bf = this.bgTypeFilter;
      if (bf && bf !== "all") {
        list = list.filter((s) => (s.bg_type || "image") === bf);
      }

      // Resource missing filter
      const rmf = this.resourceMissingFilter;
      if (rmf && rmf !== "all") {
        list = list.filter((s) => {
          const exists = this.resourceMissingMap[s.id];
          return rmf === "missing" ? exists === false : exists === true;
        });
      }

      return list;
    },
    /** 所有有来源（被其他场景跳转指向）的 sort_order 集合 */
    sceneSortOrdersWithSource() {
      const scenes = this.scenes || [];
      const sourceSet = new Set();
      // 预计算最大 sort_order，供 skip_to_end 逻辑使用
      const lastSort = scenes.length > 0 ? Math.max(...scenes.map((s) => s.sort_order)) : -1;
      scenes.forEach((s) => {
        if (s.options && s.options.length > 0) {
          // 有选项分支：运行时只有选项分支生效，默认跳转被忽略
          s.options.forEach((opt) => {
            if (opt.next_scene_sort != null) sourceSet.add(Number(opt.next_scene_sort));
            if (opt.skip_to_end && opt.next_scene_sort == null) sourceSet.add(lastSort);
          });
        } else {
          // 无选项分支：从默认跳转查找来源
          if (s.next_scene_sort != null) sourceSet.add(Number(s.next_scene_sort));
          if (s.next_scene_skip_to_end && s.next_scene_sort == null) sourceSet.add(lastSort);
        }
        // 小游戏失败跳转（与选项/默认跳转无关，是独立路径）
        if (s.game_fail_next_scene_sort != null) sourceSet.add(Number(s.game_fail_next_scene_sort));
        if (s.game_fail_next_scene_skip_to_end && s.game_fail_next_scene_sort == null) sourceSet.add(lastSort);
      });
      return sourceSet;
    },
    availableTargetScenes() {
      if (!this.selectedScene) return this.scenes;
      return this.scenes.filter((s) => s.id !== this.selectedScene.id);
    },
    filteredMentionAssets() {
      if (!this.mentionQuery) return this.allAssets.slice(0, 20);
      const q = this.mentionQuery.toLowerCase();
      return this.allAssets.filter(a => 
        (a.name || "").toLowerCase().includes(q) || 
        (a.asset_type || "").toLowerCase().includes(q)
      ).slice(0, 20);
    },
    characterAssets() {
      return this.allAssets.filter((a) => a.asset_type === "角色");
    },
    /** 当前选中场景的来源：哪些场景可以推进到它 */
    sceneSources() {
      if (!this.selectedScene) return [];
      const targetSort = this.selectedScene.sort_order;
      if (targetSort == null) return [];
      const scenes = this.scenes || [];
      // 预计算（不含自身的）最大 sort_order，供 skip_to_end 逻辑使用
      const otherScenes = scenes.filter((s) => s.id !== this.selectedScene.id);
      const lastSort = otherScenes.length > 0
        ? Math.max(...otherScenes.map((s) => s.sort_order))
        : -1;
      return scenes.filter((s) => {
        if (s.id === this.selectedScene.id) return false;
        // 有选项分支：运行时只有选项分支生效，默认跳转被忽略
        if (s.options && s.options.length > 0) {
          return s.options.some((opt) => {
            if (opt.next_scene_sort != null && Number(opt.next_scene_sort) === Number(targetSort)) return true;
            // 选项 skip_to_end 无具体目标 → 指向最后一个场景
            if (opt.skip_to_end && opt.next_scene_sort == null && Number(targetSort) === lastSort) return true;
            return false;
          });
        }
        // 无选项分支：从默认跳转查找来源
        if (s.next_scene_sort != null && Number(s.next_scene_sort) === Number(targetSort)) return true;
        if (s.next_scene_skip_to_end && s.next_scene_sort == null && Number(targetSort) === lastSort) return true;
        // 小游戏失败跳转（与选项/默认跳转无关，是独立路径）
        if (s.game_fail_next_scene_sort != null && Number(s.game_fail_next_scene_sort) === Number(targetSort)) return true;
        if (s.game_fail_next_scene_skip_to_end && s.game_fail_next_scene_sort == null && Number(targetSort) === lastSort) return true;
        return false;
      }).sort((a, b) => a.sort_order - b.sort_order);
    },
    thirdPartyGameOptions() {
      return [
        { id: 'chidouren', name: '躲避无人机' }
      ];
    }
  },
  created() {
    this.storyId = Number(this.$route.params.id);
    this._restoreEditPrefs();
    this.loadData();
    this.loadAllAssets();
  },
  beforeRouteLeave(to, from, next) {
    if (this.bgUploadAbortController) {
      this.bgUploadAbortController.abort();
      this.bgUploadAbortController = null;
    }
    if (this.annotationUploadAbortController) {
      this.annotationUploadAbortController.abort();
      this.annotationUploadAbortController = null;
    }
    next();
  },
  beforeDestroy() {
    if (this.bgUploadAbortController) {
      this.bgUploadAbortController.abort();
      this.bgUploadAbortController = null;
    }
    if (this.annotationUploadAbortController) {
      this.annotationUploadAbortController.abort();
      this.annotationUploadAbortController = null;
    }
  },
  methods: {
    resolveMediaUrl,
    _restoreEditPrefs() {
      if (!this.storyId) return;
      try {
        const raw = sessionStorage.getItem(`storyPlayEdit_prefs_${this.storyId}`);
        if (!raw) return;
        const p = JSON.parse(raw);
        if (typeof p.playFromBeginning === "boolean") {
          this.playFromBeginning = p.playFromBeginning;
        }
      } catch (e) {
        /* ignore */
      }
    },
    isSceneMissingDubbing(scene) {
      if (!scene || scene.bg_type === "video" || this.isGameBgType(scene.bg_type)) return false;
      const lines = scene.lines || [];
      if (lines.length === 0) return false;
      return lines.some((line) => {
        if (typeof line === "string") return true;
        // 既要配置了角色和情绪，也要生成了音频
        return !line.speaker_asset_id || !line.emotion || !line.audio_url;
      });
    },
    _persistEditPrefs() {
      if (!this.storyId) return;
      try {
        sessionStorage.setItem(
          `storyPlayEdit_prefs_${this.storyId}`,
          JSON.stringify({ playFromBeginning: this.playFromBeginning })
        );
      } catch (e) {
        /* ignore */
      }
    },
    sceneHasAnnotation(scene) {
      if (!scene) return false;
      const t = ((scene.annotation_text || "") + "").trim();
      const img = ((scene.annotation_image || "") + "").trim();
      return !!(t || img);
    },
    sceneHasJump(scene) {
      if (!scene) return false;
      // 直接跳转、小游戏、或选项中有跳转目标
      if (scene.next_scene_sort != null) return true;
      if (scene.mini_game_id) return true;
      if (scene.options && scene.options.length > 0) {
        return scene.options.some((opt) => opt.next_scene_sort != null);
      }
      return false;
    },
    sceneHasSource(scene) {
      if (!scene) return false;
      return this.sceneSortOrdersWithSource.has(Number(scene.sort_order));
    },
    sceneHasDub(scene) {
      if (!scene) return false;
      if (scene.bg_type === "video" || this.isGameBgType(scene.bg_type)) return true;
      const lines = scene.lines || [];
      if (lines.length === 0) return false;
      return lines.every((line) => {
        if (typeof line === "string") return false;
        return line.speaker_asset_id && line.emotion && line.audio_url;
      });
    },
    annotationStatusLabel(scene) {
      if (!this.sceneHasAnnotation(scene)) return "";
      const st = (scene.annotation_status || "pending").trim();
      if (st === "processed") return "已处理";
      if (st === "reviewing") return "等待审核";
      return "未处理";
    },
    annotationTagType(status) {
      const st = (status || "pending").trim();
      if (st === "processed") return "success";
      if (st === "reviewing") return "primary";
      return "warning";
    },
    filteredSceneIndex(scene) {
      const i = this.scenes.findIndex((s) => s.id === scene.id);
      return i >= 0 ? i + 1 : 0;
    },
    async loadData() {
      try {
        console.log("Loading story data for ID:", this.storyId);
        const [{ data: story }, { data: sceneList }, { data: miniGamesList }, { data: thirdPartyGamesList }] = await Promise.all([
          getStoryPlay(this.storyId),
          getStoryScenes(this.storyId),
          getMiniGames(),
          getThirdPartyGames()
        ]);
        console.log("Loaded story:", story);
        console.log("Loaded scenes:", sceneList);
        console.log("Loaded mini games:", miniGamesList);
        this.storyTitle = story?.title || "";
        this.scenes = sceneList || [];
        this.miniGames = miniGamesList || [];
        this.thirdPartyGames = thirdPartyGamesList || [];
        if (this.scenes.length > 0 && !this.selectedScene) {
          const sceneIdFromQuery = Number(this.$route.query.scene_id);
          const initialScene = this.scenes.find((s) => s.id === sceneIdFromQuery) || this.scenes[0];
          this.selectScene(initialScene);
        }
      } catch (e) {
        console.error("Failed to load story play edit data:", e);
        this.$message.error("加载数据失败");
      }
    },
    getScenePreview(scene) {
      if (scene.name) {
        return scene.name;
      }
      const lines = scene.lines || [];
      if (lines.length > 0) {
        const first = lines[0];
        const text = typeof first === "string" ? first : (first && first.text) || "";
        if (text) {
          return text.slice(0, 20);
        }
      }
      return "（无名称）";
    },
    getSceneNameBySort(sortOrder) {
      if (sortOrder == null) return null;
      const scene = this.sceneLookupBySort[sortOrder];
      return scene ? this.getScenePreview(scene) : `#${sortOrder}`;
    },
    normalizeSpeechRate(val) {
      if (val === undefined || val === null) {
        return 1.0;
      }
      if (val === 0) {
        return 1.0;
      }
      if (val < 0.5 || val > 2.0) {
        const floatVal = 1.0 + val / 100;
        return Math.max(0.5, Math.min(2.0, parseFloat(floatVal.toFixed(2))));
      }
      return val;
    },
    normalizeSceneLine(line) {
      if (typeof line === "string") {
        return {
          text: line,
          speaker_asset_id: null,
          speaker_name: "",
          emotion: "",
          emotion_scale: 1.0,
          speech_rate: 1.0,
          audio_url: ""
        };
      }
      return {
        text: (line && line.text) || "",
        speaker_asset_id: (line && line.speaker_asset_id) ? Number(line.speaker_asset_id) : null,
        speaker_name: (line && line.speaker_name) || "",
        emotion: (line && line.emotion) || "",
        emotion_scale: (line && line.emotion_scale) !== undefined ? line.emotion_scale : 1.0,
        speech_rate: this.normalizeSpeechRate(line && line.speech_rate),
        audio_url: (line && line.audio_url) || ""
      };
    },
    async generateLineTTS(index, autoSave = true) {
      const line = this.editorForm.lines[index];
      if (!line || !line.speaker_asset_id || !line.emotion || !line.text) {
        if (autoSave) this.$message.warning("请先配置角色、情绪和台词内容");
        return;
      }

      const asset = this.getCharacterAssetById(line.speaker_asset_id);
      if (!asset || !asset.voice_timbre) {
        if (autoSave) this.$message.warning("该角色未配置有效音色，请在资产管理中设置");
        return;
      }

      this.$set(line, "ttsLoading", true);
      try {
        const { data } = await import("@/net/api").then(api => api.synthesizeTTS({
          text: line.text,
          speaker_id: asset.voice_timbre,
          emotion: line.emotion,
          emotion_scale: line.emotion_scale || 1.0,
          speech_rate: Math.round(((line.speech_rate || 1.0) - 1.0) * 100)
        }));

        if (data && data.code === 0 && data.data && data.data.url) {
          this.$set(line, "audio_url", data.data.url);
          if (autoSave) {
            this.$message.success("配音生成成功");
            // 自动保存场景以持久化 audio_url
            await this.handleSaveScene();
          }
        } else {
          const msg = (data && data.message) || "接口未返回音频地址";
          throw new Error(msg);
        }
      } catch (e) {
        console.error("TTS generation failed:", e);
        if (autoSave) this.$message.error("生成配音失败: " + (e.message || "未知错误"));
        throw e; // Rethrow to let batch handler know
      } finally {
        this.$set(line, "ttsLoading", false);
      }
    },
    async handleBatchGenerateTTS() {
      const linesToGen = this.editorForm.lines.filter(l =>
        l.text && l.speaker_asset_id && l.emotion && !l.audio_url
      );

      if (linesToGen.length === 0) {
        this.$message.info("没有需要生成配音的台词（请检查是否已配置角色和情绪）");
        return;
      }

      this.batchTTSLoading = true;
      let successCount = 0;
      let failCount = 0;
      try {
        for (let i = 0; i < this.editorForm.lines.length; i++) {
          const line = this.editorForm.lines[i];
          // 仅处理已配置但未生成的
          if (line.text && line.speaker_asset_id && line.emotion && !line.audio_url) {
            try {
              await this.generateLineTTS(i, false);
              successCount++;
            } catch (e) {
              failCount++;
            }
          }
        }

        if (successCount > 0) {
          await this.handleSaveScene();
          this.$message.success(`生成完毕！成功生成 ${successCount} 条配音`);
        }
        if (failCount > 0) {
          this.$message.warning(`${failCount} 条配音生成失败，请检查配置或网络`);
        }
      } finally {
        this.batchTTSLoading = false;
      }
    },
    async handleGlobalBatchTTS() {
      const scenesToProcess = this.scenes.filter((s) => this.isSceneMissingDubbing(s));
      if (scenesToProcess.length === 0) {
        this.$message.info("所有场景的配音已完成或未配置角色/情绪");
        return;
      }

      try {
        await this.$confirm(
          `确定要为全剧 ${scenesToProcess.length} 个配音不完整的场景生成配音吗？这可能需要一些时间。`,
          "提示",
          { type: "warning" }
        );
      } catch (e) {
        return;
      }

      this.globalBatchTTSLoading = true;
      let totalLinesGenerated = 0;
      let scenesUpdated = 0;

      try {
        const api = await import("@/net/api");
        for (const scene of scenesToProcess) {
          let sceneChanged = false;
          const lines = Array.isArray(scene.lines) ? scene.lines : [];

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (
              line &&
              typeof line === "object" &&
              line.text &&
              line.speaker_asset_id &&
              line.emotion &&
              !line.audio_url
            ) {
              const asset = this.getCharacterAssetById(line.speaker_asset_id);
              if (asset && asset.voice_timbre) {
                try {
                  const { data: ttsRes } = await api.synthesizeTTS({
                    text: line.text,
                    speaker_id: asset.voice_timbre,
                    emotion: line.emotion,
                    emotion_scale: line.emotion_scale || 1.0,
                    speech_rate: Math.round(((line.speech_rate || 1.0) - 1.0) * 100)
                  });
                  if (ttsRes && ttsRes.code === 0 && ttsRes.data && ttsRes.data.url) {
                    line.audio_url = ttsRes.data.url;
                    totalLinesGenerated++;
                    sceneChanged = true;
                  }
                } catch (err) {
                  console.error(`Global TTS failed for scene ${scene.id} line ${i}`, err);
                }
              }
            }
          }

          if (sceneChanged) {
            // 只更新配音内容，不碰场景其他字段
            await api.updateStoryScene(scene.id, {
              lines: scene.lines
            });
            scenesUpdated++;
            // 如果当前编辑的正是这个场景，更新编辑器状态
            if (this.selectedScene && this.selectedScene.id === scene.id) {
              this.editorForm.lines = [...scene.lines];
            }
          }
        }
        this.$message.success(
          `全剧配音生成完毕！共更新 ${scenesUpdated} 个场景，生成 ${totalLinesGenerated} 条配音。`
        );
      } catch (e) {
        console.error("Global Batch TTS failed", e);
        this.$message.error("全剧配音过程中发生错误");
      } finally {
        this.globalBatchTTSLoading = false;
      }
    },
    async handleCheckResources() {
      this.resourceChecking = true;
      this.resourceMissingMap = {};
      try {
        const { data } = await checkStoryPlayResources(this.storyId);
        if (data && typeof data === "object") {
          this.resourceMissingMap = data;
          const total = Object.keys(data).length;
          const missing = Object.values(data).filter(v => !v).length;
          if (missing === 0) {
            this.$message.success(`资源检查完成：${total} 个场景的资源均存在`);
          } else {
            this.$message.warning(`资源检查完成：${missing}/${total} 个场景的资源缺失`);
          }
        }
      } catch (e) {
        this.$message.error("资源检查失败");
      } finally {
        this.resourceChecking = false;
      }
    },
    playLineTTS(line) {
      if (!line.audio_url) return;
      const url = this.resolveMediaUrl(line.audio_url);
      const audio = new Audio(url);
      audio.play().catch(e => {
        console.error("Playback failed:", e);
        this.$message.error("播放失败");
      });
    },
    sceneBgIsVideo(scene) {
      return !!(scene && (scene.bg_type || "image") === "video");
    },
    isGameBgType(type) {
      return type === 'minigame' || type === 'third_party_game';
    },
    getBgTypeLabel(type) {
      if (type === 'minigame') return '小游戏';
      if (type === 'third_party_game') return '三方游戏';
      if (type === 'video') return '视频';
      return '图片';
    },
    getBgTypeTagType(type) {
      if (type === 'minigame' || type === 'third_party_game') return 'warning';
      if (type === 'video') return 'success';
      return 'info';
    },

    handleEditorBgTypeChange() {
      this.editorForm.bg_image = "";
    },
    handleThirdPartyGameChange(id) {
      const game = this.thirdPartyGames.find(g => g.game_id === id);
      this.editorForm.third_party_game_name = game ? game.name : "";
    },

    parseEmotionOptions(raw) {
      if (Array.isArray(raw)) {
        return Array.from(new Set(raw.map((item) => (item && item.name ? String(item.name).trim() : "")).filter(Boolean)));
      }
      const text = (raw || "").trim();
      if (!text) {
        return [];
      }
      if (text.startsWith("[")) {
        try {
          const parsed = JSON.parse(text);
          if (Array.isArray(parsed)) {
            return Array.from(new Set(parsed.map((item) => (item && item.name ? String(item.name).trim() : "")).filter(Boolean)));
          }
        } catch (e) {
          return [];
        }
      }
      return [];
    },
    getCharacterAssetById(assetId) {
      if (!assetId) return undefined;
      return this.characterAssets.find((a) => String(a.id) === String(assetId));
    },
    getLineEmotionOptions(line) {
      // 情绪从所有的情绪里选
      const options = Object.keys(this.emotionMap);
      if (line.emotion) {
        options.push(line.emotion);
      }
      return Array.from(new Set(options));
    },
    handleLineSpeakerChange(index) {
      const line = this.editorForm.lines[index];
      if (!line) {
        return;
      }
      const speaker = this.getCharacterAssetById(line.speaker_asset_id);
      line.speaker_name = speaker ? speaker.name : "";
      this.handleLineContentChange(index);
    },
    handleLineContentChange(index) {
      const line = this.editorForm.lines[index];
      if (line && line.audio_url) {
        line.audio_url = "";
      }
    },
    selectScene(scene) {
      this.selectedScene = scene;
      const annSt = scene.annotation_status;
      const hasAnn = ((scene.annotation_text || "") + "").trim() || ((scene.annotation_image || "") + "").trim();
      this.editorForm = {
        bg_type: scene.bg_type || "image",
        bg_image: scene.bg_image || "",
        lines: Array.isArray(scene.lines) ? scene.lines.map((line) => this.normalizeSceneLine(line)) : [],
        options: Array.isArray(scene.options)
          ? scene.options.map((o) => ({ text: o.text || "", next_scene_sort: o.next_scene_sort || null, skip_to_end: !!o.skip_to_end }))
          : [],
        next_scene_sort: scene.next_scene_sort || null,
        next_scene_skip_to_end: !!scene.next_scene_skip_to_end,
        name: scene.name || "",
        bg_prompt: scene.bg_prompt || "",
        annotation_text: scene.annotation_text || "",
        annotation_image: scene.annotation_image || "",
        annotation_status: hasAnn ? (["pending", "reviewing", "processed"].includes(annSt) ? annSt : "pending") : "pending",
        mini_game_id: scene.mini_game_id || null,
        mini_game_desc: scene.mini_game_desc || "",
        third_party_game_id: scene.third_party_game_id || "",
        third_party_game_name: scene.third_party_game_name || "",
        game_fail_next_scene_sort: scene.game_fail_next_scene_sort || null,
        game_fail_next_scene_skip_to_end: !!scene.game_fail_next_scene_skip_to_end
      };
      this.$nextTick(() => {
        const el = document.querySelector(".scene-list-body .scene-item.active");
        if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
    async handleAddScene() {
      try {
        const payload = {
          sort_order: this.scenes.length + 1,
          lines: [],
          options: [],
          bg_type: "image",
          bg_image: "",
          name: "",
          bg_prompt: "",
          annotation_text: "",
          annotation_image: "",
          annotation_status: null
        };
        const { data } = await createStoryScene(this.storyId, payload);
        const newScene = data || { ...payload, id: Date.now() };
        this.scenes.push(newScene);
        this.selectScene(newScene);
      } catch (e) {
        this.$message.error("添加场景失败");
      }
    },
    handleDeleteSceneById(sceneId) {
      const index = this.scenes.findIndex((s) => s.id === sceneId);
      if (index >= 0) this.handleDeleteScene(index);
    },
    async handleDeleteScene(index) {
      const scene = this.scenes[index];
      if (!scene) return;
      try {
        await this.$confirm("确定删除该场景吗？", "提示", { type: "warning" });
        if (scene.id) {
          await deleteStoryScene(scene.id);
        }
        this.scenes.splice(index, 1);
        if (this.selectedScene && this.selectedScene.id === scene.id) {
          this.selectedScene = null;
          if (this.scenes.length > 0) {
            const nextIndex = Math.min(index, this.scenes.length - 1);
            this.selectScene(this.scenes[nextIndex]);
          }
        }
        await this.saveOrder();
      } catch (e) {
        if (e !== "cancel") this.$message.error("删除失败");
      }
    },
    handleChangeSceneOrder(index) {
      const maxIndex = this.scenes.length;
      if (maxIndex <= 1) {
        this.$message.info("只有一个场景，无需调整顺序");
        return;
      }
      const scene = this.scenes[index];
      this._promptSceneOrder(scene, index, maxIndex);
    },
    handleChangeSceneOrderBySceneId(sceneId) {
      const index = this.scenes.findIndex((s) => s.id === sceneId);
      if (index < 0) return;
      this.handleChangeSceneOrder(index);
    },
    async _promptSceneOrder(scene, currentIndex, maxIndex) {
      try {
        const { value } = await this.$prompt(
          `请输入新的顺序号（1 ~ ${maxIndex}），当前为 ${currentIndex + 1}`,
          "修改顺序",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            inputPattern: /^[1-9]\d*$/,
            inputErrorMessage: "请输入正整数",
            inputValue: String(currentIndex + 1),
          }
        );
        const targetIndex = parseInt(value, 10) - 1; // 0-based
        if (targetIndex === currentIndex) return;
        if (targetIndex < 0 || targetIndex >= maxIndex) {
          this.$message.warning(`顺序号需要在 1 到 ${maxIndex} 之间`);
          return;
        }
        // Move scene in array
        const [moved] = this.scenes.splice(currentIndex, 1);
        this.scenes.splice(targetIndex, 0, moved);
        await this.saveOrder();
        // Re-select the moved scene
        const updated = this.scenes.find((s) => s.id === scene.id);
        if (updated) {
          this.selectScene(updated);
        }
        this.$message.success(`顺序已改为 ${targetIndex + 1}`);
      } catch (e) {
        // cancelled
      }
    },
    handleDragEnd() {
      this.saveOrder();
    },
    async saveOrder() {
      try {
        const ids = this.scenes.map((s) => s.id).filter(Boolean);
        await reorderStoryScenes(this.storyId, { scene_ids: ids });
        const { data: sceneList } = await getStoryScenes(this.storyId);
        this.scenes = sceneList || [];
        if (this.selectedScene) {
          const updated = this.scenes.find((s) => s.id === this.selectedScene.id);
          if (updated) {
            this.selectScene(updated);
          }
        }
      } catch (e) {
        this.$message.error("保存顺序失败");
      }
    },
    async handleSaveScene() {
      if (!this.selectedScene || this.annotationUploading) return;
      this.saving = true;
      try {
        const payload = {
          bg_type: this.editorForm.bg_type || "image",
          bg_image: this.editorForm.bg_image,
          lines: this.editorForm.lines.map((line) => ({
            text: (line.text || "").trim(),
            speaker_asset_id: line.speaker_asset_id || null,
            speaker_name: line.speaker_name || "",
            emotion: (line.emotion || "").trim(),
            emotion_scale: line.emotion_scale !== undefined ? line.emotion_scale : 1.0,
            speech_rate: line.speech_rate !== undefined ? line.speech_rate : 1.0,
            audio_url: line.audio_url || ""
          })),
          options: this.editorForm.options.map((o) => ({
            text: o.text,
            next_scene_id: o.next_scene_sort
              ? this.scenes.find((s) => s.sort_order === o.next_scene_sort)?.id || null
              : null,
            skip_to_end: !!o.skip_to_end
          })),
          next_scene_id: this.editorForm.next_scene_sort
            ? this.scenes.find((s) => s.sort_order === this.editorForm.next_scene_sort)?.id || null
            : null,
          next_scene_skip_to_end: !!this.editorForm.next_scene_skip_to_end,
          game_fail_next_scene_id: this.editorForm.game_fail_next_scene_sort
            ? this.scenes.find((s) => s.sort_order === this.editorForm.game_fail_next_scene_sort)?.id || null
            : null,
          game_fail_next_scene_skip_to_end: !!this.editorForm.game_fail_next_scene_skip_to_end,
          mini_game_id: this.editorForm.bg_type === 'third_party_game' ? null : (this.editorForm.mini_game_id || null),
          mini_game_desc: this.editorForm.mini_game_desc,
          third_party_game_id: this.editorForm.bg_type === 'third_party_game' ? this.editorForm.third_party_game_id : null,
          third_party_game_name: this.editorForm.bg_type === 'third_party_game' ? this.editorForm.third_party_game_name : null,
          name: this.editorForm.name,
          bg_prompt: this.editorForm.bg_prompt,
          annotation_text: this.editorForm.annotation_text,
          annotation_image: this.editorForm.annotation_image,
          annotation_status: this.annotationEditorHasContent ? this.editorForm.annotation_status : null
        };
        const { data } = await updateStoryScene(this.selectedScene.id, payload);
        this.$message.success("保存成功");
        if (data && data.bg_image) {
          this.editorForm.bg_image = data.bg_image;
        }
        if (data && data.bg_type) {
          this.editorForm.bg_type = data.bg_type;
        }
        if (data && data.annotation_image) {
          this.editorForm.annotation_image = data.annotation_image;
        }
        const { data: sceneList } = await getStoryScenes(this.storyId);
        this.scenes = sceneList || [];
        const updated = this.scenes.find((s) => s.id === this.selectedScene.id);
        if (updated) {
          this.selectScene(updated);
        }
      } catch (e) {
        const errMsg = e.response?.data?.message || "保存失败";
        this.$message.error(errMsg);
      } finally {
        this.saving = false;
      }
    },
    addLine() {
      this.editorForm.lines.push({
        text: "",
        speaker_asset_id: null,
        speaker_name: "",
        emotion: "",
        emotion_scale: 1.0,
        speech_rate: 1.0,
        audio_url: "",
        ttsLoading: false
      });
    },
    removeLine(index) {
      this.editorForm.lines.splice(index, 1);
    },
    addOption() {
      this.editorForm.options.push({ text: "", next_scene_sort: null, skip_to_end: false });
    },
    removeOption(index) {
      this.editorForm.options.splice(index, 1);
    },
    async handleBgUpload({ file }) {
      if (this.bgUploadAbortController) {
        this.bgUploadAbortController.abort();
      }
      this.bgUploadAbortController = new AbortController();
      this.bgUploading = true;
      this.bgUploadProgress = 0;
      try {
        const isVideo = this.editorForm.bg_type === "video";
        const timeout = isVideo ? 600000 : 120000;
        const uploadFn = isVideo ? uploadStoryVideo : uploadStoryImage;
        const { data } = await uploadFn(
          file,
          (progress) => {
            this.bgUploadProgress = progress;
          },
          { signal: this.bgUploadAbortController.signal, timeout }
        );
        this.editorForm.bg_image = data.url;
        this.$message.success("背景上传成功");
      } catch (e) {
        if (e.name === "CanceledError" || e.name === "AbortError") {
          console.log("Background upload canceled");
        } else {
          this.$message.error("上传失败");
        }
      } finally {
        this.bgUploading = false;
        this.bgUploadAbortController = null;
      }
    },
    triggerBgUpload() {
      const uploadEl = this.$refs.bgUpload;
      if (uploadEl) {
        const input = uploadEl.$el.querySelector('input[type="file"]');
        if (input) input.click();
      }
    },
    async handleAnnotationUpload({ file }) {
      if (this.annotationUploadAbortController) {
        this.annotationUploadAbortController.abort();
      }
      this.annotationUploadAbortController = new AbortController();
      this.annotationUploading = true;
      try {
        const { data } = await uploadStoryImage(file, null, {
          signal: this.annotationUploadAbortController.signal
        });
        this.editorForm.annotation_image = data.url;
        this.$message.success("批注图已上传（保存场景后入库）");
      } catch (e) {
        if (e.name === "CanceledError" || e.name === "AbortError") {
          return;
        }
        this.$message.error("上传失败");
      } finally {
        this.annotationUploading = false;
        this.annotationUploadAbortController = null;
      }
    },
    handlePreview() {
      const sceneId = this.selectedScene && this.selectedScene.id;
      if (this.playFromBeginning) {
        this.$router.push(`/story-plays/${this.storyId}/play?skip_selector=1&return_to=edit&scene_id=${sceneId}`);
      } else {
        const idx = this.scenes.findIndex(s => s.id === this.selectedScene.id);
        this.$router.push(`/story-plays/${this.storyId}/play?skip_selector=1&start=${idx}&return_to=edit&scene_id=${sceneId}`);
      }
    },
    async loadAllAssets() {
      try {
        const { data } = await getAssets({ per_page: 1000 });
        this.allAssets = data.items || data || [];
      } catch (e) {
        console.error("Failed to load assets for mentions", e);
      }
    },
    handlePromptInput() {
      this.$nextTick(() => {
        const input = this.$refs.bgPromptInput.$refs.textarea;
        const selectionStart = input.selectionStart;
        const textBefore = this.editorForm.bg_prompt.substring(0, selectionStart);
        
        const lastAtIndex = textBefore.lastIndexOf("@");
        if (lastAtIndex !== -1) {
          const query = textBefore.substring(lastAtIndex + 1);
          if (!query.includes(" ") && !query.includes("\n")) {
            this.mentionQuery = query;
            this.mentionVisible = true;
            this.mentionIndex = 0;
            this.calculateMentionPosition(input, lastAtIndex);
            return;
          }
        }
        this.mentionVisible = false;
      });
    },
    calculateMentionPosition(input, index) {
      const mirror = this.$refs.mentionMirror;
      if (!mirror) return;
      const styles = window.getComputedStyle(input);
      
      mirror.style.width = styles.width;
      mirror.style.padding = styles.padding;
      mirror.style.fontSize = styles.fontSize;
      mirror.style.fontFamily = styles.fontFamily;
      mirror.style.lineHeight = styles.lineHeight;
      mirror.style.border = styles.border;
      
      const textBefore = this.editorForm.bg_prompt.substring(0, index);
      mirror.textContent = textBefore;
      
      const marker = document.createElement("span");
      marker.textContent = "@";
      mirror.appendChild(marker);
      
      const lineHeight = parseInt(styles.lineHeight) || 20;
      this.mentionPosition = {
        top: marker.offsetTop + lineHeight + 2,
        left: Math.min(marker.offsetLeft, parseInt(styles.width) - 160)
      };
    },
    handlePromptKeyDown(e) {
      if (!this.mentionVisible) return;
      
      const list = this.filteredMentionAssets;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.mentionIndex = (this.mentionIndex + 1) % list.length;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.mentionIndex = (this.mentionIndex - 1 + list.length) % list.length;
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (list.length > 0) {
          e.preventDefault();
          this.selectMention(list[this.mentionIndex]);
        } else {
          this.mentionVisible = false;
        }
      } else if (e.key === "Escape") {
        this.mentionVisible = false;
      }
    },
    selectMention(asset) {
      const input = this.$refs.bgPromptInput.$refs.textarea;
      const selectionStart = input.selectionStart;
      const textBefore = this.editorForm.bg_prompt.substring(0, selectionStart);
      const textAfter = this.editorForm.bg_prompt.substring(selectionStart);
      
      const lastAtIndex = textBefore.lastIndexOf("@");
      // Replace @xxx with just the asset name (no @)
      const newTextBefore = textBefore.substring(0, lastAtIndex) + asset.name + " ";
      
      this.editorForm.bg_prompt = newTextBefore + textAfter;
      this.mentionVisible = false;
      
      this.$nextTick(() => {
        input.focus();
        const newPos = newTextBefore.length;
        input.setSelectionRange(newPos, newPos);
      });
    },
    async handleDownloadAssets() {
      if (!this.selectedScene || !this.editorForm.bg_prompt) {
        this.$message.warning("请先输入提示词");
        return;
      }
      this.downloadingAssets = true;
      try {
        const { data } = await downloadSceneAssets(this.selectedScene.id, {
          prompt: this.editorForm.bg_prompt
        });
        if (data && data.url) {
          const href = data.url.startsWith("http") ? data.url : `${window.location.origin}${data.url}`;
          window.open(href, "_blank");
          this.$message.success("打包成功，已开始下载");
        } else {
          this.$message.error("服务器未返回下载地址");
        }
      } catch (e) {
        const msg = e.response?.data?.message || "打包下载失败";
        this.$message.error(msg);
      } finally {
        this.downloadingAssets = false;
      }
    }
  }
};
</script>

<style scoped>
.story-edit-page {
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.edit-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.edit-body {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 16px;
  overflow: hidden;
}
.scene-list {
  width: 280px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.scene-list-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
}
.scene-list-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.scene-item {
  padding: 10px 12px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.scene-item.active {
  background: #ecf5ff;
}
.scene-item:hover .scene-delete {
  opacity: 1;
}
.scene-index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}
.scene-preview {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}
.scene-delete {
  color: #f56c6c;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
  font-size: 14px;
}
.scene-order-btn {
  color: #409eff;
  opacity: 0;
  transition: opacity 0.2s;
  padding: 0 4px;
  font-size: 12px;
  flex-shrink: 0;
}
.scene-item:hover .scene-order-btn {
  opacity: 1;
}
.scene-editor {
  position: relative;
  flex: 1;
  min-height: 0;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.scene-editor-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
  z-index: 5;
}
.scene-editor-body {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 16px;
  box-sizing: border-box;
}
.scene-editor-save-float {
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.scene-editor-title {
  margin: 0;
  flex: 1;
  min-width: 0;
  line-height: 32px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.line-input-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: flex-start;
}
.line-config-main {
  flex: 1;
}
.line-voice-row {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
}
.line-tts-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}
.line-scale-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 8px;
  flex-shrink: 0;
}
.line-speed-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 8px;
  flex-shrink: 0;
}
.line-speed-wrap :deep(.el-slider__runway) {
  margin: 12px 0;
}
.scale-label {
  font-size: 12px;
  color: #909399;
}
.option-input-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}
.bg-upload-trigger-hidden {
  position: fixed;
  left: 0;
  top: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.bg-uploader-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.bg-uploader {
  width: 200px;
  height: 120px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
}
.bg-uploader:hover {
  border-color: #409eff;
}
.bg-uploader-filled {
  cursor: zoom-in;
  border-style: solid;
}
.bg-upload-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.bg-uploader-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.bg-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}
.bg-preview-el {
  width: 200px;
  height: 120px;
  display: block;
}
.bg-preview-video {
  object-fit: cover;
}
.bg-type-radio {
  display: block;
  margin-bottom: 8px;
}
.scene-list-toolbar {
  flex-wrap: wrap;
  gap: 8px;
}
.scene-annotation-filter,
.scene-jump-filter,
.scene-source-filter,
.scene-resource-filter,
.scene-dub-filter,
.scene-bg-type-filter,
.scene-missing-filter {
  width: 112px !important;
  flex-shrink: 0;
}
.scene-thumb-wrap {
  flex-shrink: 0;
}
.scene-thumb {
  width: 38px;
  height: 38px;
  border-radius: 4px;
}
.scene-thumb :deep(img) {
  object-fit: cover;
}
.scene-thumb-video {
  object-fit: cover;
  vertical-align: top;
}
.scene-no-bg-icon {
  flex-shrink: 0;
  margin-right: 2px;
}
.scene-preview-block {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}
.scene-ann-tag,
.scene-source-tag,
.scene-missing-tag {
  flex-shrink: 0;
}
.scene-index-static {
  background: #909399 !important;
}
.scene-list-static {
  display: flex;
  flex-direction: column;
}
.scene-list-empty-tip {
  padding: 24px 12px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}
.scene-status-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}
.scene-type-tag {
  font-size: 10px;
  padding: 0 4px;
  height: 18px;
  line-height: 16px;
}
.scene-dubbing-warning {
  color: #E6A23C;
  font-size: 14px;
  cursor: help;
}
.scene-annotation-editor {
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.annotation-img-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
}
.annotation-editor-img {
  width: 120px;
  height: 80px;
  border-radius: 4px;
}
.annotation-editor-img :deep(img) {
  object-fit: cover;
}
.upload-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* Mention Styles */
.mention-wrapper {
  position: relative;
}
.mention-list {
  position: absolute;
  z-index: 3000;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  max-height: 200px;
  overflow-y: auto;
  min-width: 160px;
}
.mention-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  transition: background 0.2s;
}
.mention-item:hover, .mention-item.active {
  background-color: #f5f7fa;
  color: #409eff;
}
.mention-item-type {
  font-size: 11px;
  padding: 1px 4px;
  background: #eee;
  border-radius: 3px;
  color: #999;
}
.mention-item-name {
  font-weight: 500;
}
.mention-empty {
  padding: 12px;
  color: #999;
  font-size: 12px;
  text-align: center;
}
.mention-mirror {
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: inherit;
  border: 1px solid transparent;
  box-sizing: border-box;
}

/* Scene flow indicator in left panel */
.scene-flow-row {
  margin-top: 4px;
  padding-top: 3px;
  border-top: 1px dashed #e8e8e8;
  font-size: 11px;
  line-height: 1.5;
}
.scene-opt-flow {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-wrap: nowrap;
  white-space: nowrap;
  overflow: hidden;
}
.flow-prefix {
  color: #e6a23c;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 24px;
}
.flow-text {
  color: #606266;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
}
.flow-arrow {
  color: #c0c4cc;
  margin: 0 2px;
  flex-shrink: 0;
}
.flow-target {
  color: #409eff;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
}
.flow-target-unknown {
  color: #c0c4cc;
}
.flow-unknown {
  color: #909399;
}
.flow-tail-suffix {
  color: #909399;
  font-size: 10px;
  margin-left: 1px;
  flex-shrink: 0;
}
</style>
